import React, { useState } from "react";
import * as finService from "../../services/finService";
import useFinOptions from "./useFinOptions";
import useGlContext, { fmtMoney } from "./gl/glContext";
import { DataTable, act, hint, linkBtn } from "./gl/glUi";
import { DetailGrid, EmptyState, FormDrawer, LoadState, PageHeader, PrimaryButton, SecondaryButton, SlidePanel, Tabs, formatDate, pageStyle, todayYmd, useLoad } from "../../components/ui";

// Fixed Assets: the register (cost, depreciation, book value), additions by hand or from supplier
// bill lines coded to asset clearing, the monthly depreciation run, and transfer / revaluation /
// retirement (the last two go through approval).

const label = (s) => String(s || "").charAt(0) + String(s || "").slice(1).toLowerCase().replace(/_/g, " ");

function AssetView({ id, onHide, onChanged }) {
  const { data: a, loading, error, reload } = useLoad(() => finService.getAsset(id), [id]);
  const fin = useFinOptions(["locations"]);
  const customers = useLoad(() => finService.listCustomers({ size: 300 }), []);
  const [action, setAction] = useState(null);
  const refresh = () => {
    reload();
    onChanged();
  };
  const fields = {
    transfer: [
      { key: "location_id", label: "New location", type: "select", required: true, options: fin.locations || [] },
      { key: "date", label: "Date", type: "date" },
    ],
    revalue: [
      { key: "new_value", label: "Fair value", type: "number", step: "any", min: 0, required: true },
      { key: "date", label: "Date", type: "date" },
      { key: "reason", label: "Basis (valuer's report …)", max: 200 },
    ],
    retire: [
      { key: "date", label: "Date", type: "date" },
      { key: "proceeds", label: "Sale proceeds (0 if scrapped)", type: "number", step: "any", min: 0 },
      { key: "customer_account_id", label: "Sold to (invoice the buyer)", type: "select", options: (customers.data || []).map((c) => ({ label: `${c.name} (${c.account_number})`, value: c.id })) },
      { key: "tax_rate_code", label: "VAT code on the sale", max: 24 },
      { key: "reason", label: "Reason", max: 200 },
    ],
  };
  return (
    <SlidePanel show onHide={onHide} title={a ? `${a.asset_number} · ${a.name}` : "Asset"} width={900}>
      <LoadState loading={loading} error={error} onRetry={reload}>
        {a ? (
          <>
            {a.status !== "RETIRED" ? (
              <div className="d-flex gap-2 mb-3">
                <SecondaryButton onClick={() => setAction("transfer")}>Transfer</SecondaryButton>
                <SecondaryButton onClick={() => setAction("revalue")}>Revalue</SecondaryButton>
                <SecondaryButton onClick={() => setAction("retire")}>Retire / sell</SecondaryButton>
              </div>
            ) : null}
            <DetailGrid
              items={[
                ["Category", `${a.category_code} – ${a.category_name}`],
                ["Location", `${a.location_code} – ${a.location_name}`],
                ["In service", formatDate(a.in_service_date)],
                ["Method / life", `${a.method === "DB" ? `Declining ${Number(a.db_rate_pct)}%` : "Straight line"}, ${a.life_months} months (${a.months_depreciated} done)`],
                ["Cost", fmtMoney(a.cost)],
                ["Accumulated depreciation", fmtMoney(a.accumulated_depreciation)],
                ["Net book value", fmtMoney(a.nbv)],
                ["Revaluation reserve", fmtMoney(a.revaluation_reserve)],
                ["Tag / serial", [a.tag_number, a.serial_number].filter(Boolean).join(" / ") || "—"],
                ["Status", label(a.status)],
              ]}
            />
            <h6 className="mt-3">History</h6>
            <DataTable
              rows={a.transactions}
              columns={[
                { label: "Date", render: (t) => formatDate(t.trx_date) },
                { label: "What", render: (t) => label(t.trx_type) },
                { label: "Amount", num: true, render: (t) => fmtMoney(t.amount) },
                { label: "Cost after", num: true, render: (t) => fmtMoney(t.cost_after) },
                { label: "Depreciation after", num: true, render: (t) => fmtMoney(t.accum_after) },
                { label: "Status", render: (t) => label(t.status) },
              ]}
            />
          </>
        ) : null}
      </LoadState>
      <FormDrawer
        show={!!action}
        title={action ? label(action) : ""}
        initial={{ date: todayYmd() }}
        onHide={() => setAction(null)}
        onDone={refresh}
        onSave={(clean) => finService.assetAction(id, action, { ...clean, new_value: clean.new_value === undefined ? undefined : Number(clean.new_value), proceeds: clean.proceeds === undefined ? undefined : Number(clean.proceeds || 0) })}
        fields={action ? fields[action] : []}
      />
    </SlidePanel>
  );
}

function Register({ reloadKey, onOpen }) {
  const [status, setStatus] = useState("");
  const { data, loading, error, reload } = useLoad(() => finService.listAssets({ status: status || undefined }), [status, reloadKey]);
  const t = data ? data.totals : null;
  return (
    <>
      <div className="d-flex gap-2 mb-2">
        {["", "ACTIVE", "FULLY_DEPRECIATED", "RETIRED"].map((s) => (
          <button key={s || "all"} type="button" className={`btn btn-sm ${status === s ? "btn-primary" : "btn-outline-secondary"}`} onClick={() => setStatus(s)}>
            {s ? label(s) : "All"}
          </button>
        ))}
      </div>
      <LoadState loading={loading} error={error} onRetry={reload}>
        <DataTable
          rows={data ? data.assets : []}
          onRowClick={(r) => onOpen(r.id)}
          empty={<EmptyState title="No assets" />}
          columns={[
            { label: "Asset", render: (r) => <b>{r.asset_number}</b> },
            { label: "Name", render: (r) => r.name },
            { label: "Category", render: (r) => r.category_code },
            { label: "Location", render: (r) => r.location_code },
            { label: "In service", render: (r) => formatDate(r.in_service_date) },
            { label: "Cost", num: true, render: (r) => fmtMoney(r.cost) },
            { label: "Depreciation", num: true, render: (r) => fmtMoney(r.accumulated_depreciation) },
            { label: "Book value", num: true, render: (r) => fmtMoney(r.nbv) },
            { label: "Status", render: (r) => label(r.status) },
          ]}
        />
        {t ? (
          <div style={{ ...hint, textAlign: "right", marginTop: 6 }}>
            In use: cost {fmtMoney(t.cost)} · depreciation {fmtMoney(t.accumulated_depreciation)} · book value <b>{fmtMoney(t.nbv)}</b>
          </div>
        ) : null}
      </LoadState>
    </>
  );
}

function Runs({ reloadKey }) {
  const gl = useGlContext();
  const { data, loading, error, reload } = useLoad(() => finService.listDepreciationRuns(), [reloadKey]);
  const [month, setMonth] = useState(todayYmd().slice(0, 7));
  const run = () => gl.ledger && act(() => finService.runDepreciation({ ledger_id: gl.ledger.id, period_month: month })).then(reload);
  return (
    <>
      <div className="d-flex gap-2 align-items-center mb-3">
        <input type="month" className="form-control" style={{ width: 180 }} value={month} onChange={(e) => setMonth(e.target.value)} aria-label="Month" />
        <PrimaryButton onClick={run}>Run depreciation</PrimaryButton>
        <span style={hint}>Once per month and ledger, while the month is open for fixed assets. Months an asset missed are caught up.</span>
      </div>
      <LoadState loading={loading} error={error} onRetry={reload}>
        <DataTable
          rows={data || []}
          empty={<EmptyState title="No depreciation runs yet" />}
          columns={[
            { label: "Month", render: (r) => r.period_month },
            { label: "Assets", num: true, render: (r) => r.assets },
            { label: "Depreciation", num: true, render: (r) => fmtMoney(r.total) },
            { label: "Run on", render: (r) => formatDate(r.created_at) },
          ]}
        />
      </LoadState>
    </>
  );
}

function Pending({ reloadKey, onAdd }) {
  const { data, loading, error, reload } = useLoad(() => finService.pendingAssetAdditions(), [reloadKey]);
  return (
    <LoadState loading={loading} error={error} onRetry={reload}>
      <div style={{ ...hint, marginBottom: 8 }}>Supplier bill lines coded to the asset clearing account, waiting to become assets.</div>
      <DataTable
        rows={data || []}
        empty={<EmptyState title="Nothing waiting" />}
        columns={[
          { label: "Bill", render: (r) => `${r.bill_number} (${r.supplier_invoice_number})` },
          { label: "Supplier", render: (r) => r.supplier_name },
          { label: "Line", render: (r) => r.description },
          { label: "Amount", num: true, render: (r) => fmtMoney(r.amount_ledger) },
          {
            label: "",
            render: (r) => (
              <button type="button" style={linkBtn} onClick={() => onAdd(r)}>
                Make asset
              </button>
            ),
          },
        ]}
      />
    </LoadState>
  );
}

export default function FixedAssets() {
  const [tab, setTab] = useState("register");
  const [reloadKey, setReloadKey] = useState(0);
  const [open, setOpen] = useState(null);
  const [adding, setAdding] = useState(null);
  const fin = useFinOptions(["asset-categories", "locations"]);
  const bump = () => setReloadKey((k) => k + 1);
  return (
    <div style={pageStyle}>
      <PageHeader title="Fixed Assets" subtitle="The asset register, depreciation and disposals" actions={<PrimaryButton onClick={() => setAdding({})}>Add asset</PrimaryButton>} />
      <Tabs
        tabs={[
          { key: "register", label: "Register" },
          { key: "pending", label: "From bills" },
          { key: "runs", label: "Depreciation" },
        ]}
        active={tab}
        onChange={setTab}
      />
      {tab === "register" ? <Register reloadKey={reloadKey} onOpen={setOpen} /> : null}
      {tab === "pending" ? <Pending reloadKey={reloadKey} onAdd={(line) => setAdding({ ap_line_id: line.id, name: line.description, cost: line.amount_ledger })} /> : null}
      {tab === "runs" ? <Runs reloadKey={reloadKey} /> : null}
      {open ? <AssetView id={open} onHide={() => setOpen(null)} onChanged={bump} /> : null}
      <FormDrawer
        show={!!adding}
        title={adding && adding.ap_line_id ? "Asset from a bill line" : "Add an asset"}
        initial={{ in_service_date: todayYmd(), ...(adding || {}) }}
        onHide={() => setAdding(null)}
        onDone={bump}
        onSave={(clean) => finService.createAsset({ ...clean, ap_line_id: adding.ap_line_id, cost: clean.cost === undefined ? undefined : Number(clean.cost), salvage_value: clean.salvage_value === undefined ? undefined : Number(clean.salvage_value) })}
        fields={[
          { key: "category_id", label: "Category", type: "select", required: true, options: fin["asset-categories"] || [] },
          { key: "name", label: "Name", required: true, max: 200 },
          { key: "location_id", label: "Location", type: "select", required: true, options: fin.locations || [] },
          { key: "in_service_date", label: "In service from", type: "date", required: true },
          ...(adding && adding.ap_line_id ? [] : [{ key: "cost", label: "Cost", type: "number", step: "any", min: 0, required: true }]),
          { key: "salvage_value", label: "Salvage value (blank: the category's %)", type: "number", step: "any", min: 0 },
          { key: "tag_number", label: "Tag number", max: 40 },
          { key: "serial_number", label: "Serial number", max: 60 },
        ]}
      />
    </div>
  );
}
